import React, { createContext, useContext, useMemo, useState } from 'react';
import {
  ArchivalAidMetadata,
  ArchivalCollection,
  ArchivalUnitCopy,
  DomainObject,
  EadNode,
  Originator,
  Params,
} from 'types';
import { GridBreadcrumb, GridEntityConfig } from '../grid-types';
import { archivalAidConfig } from 'modules/archival-aid/archival-aid-grid';
import { archivalUnitConfig } from 'modules/archival-unit/archival-unit-grid';
import { eadConfig } from 'modules/archival-aid/archival-aid-children/ead-grid-config';
import { archivalCollectionConfig } from 'modules/archival-collection/archival-collection-grid';

export type ActiveEntityType = 'fund' | 'aid' | 'ead' | 'archival';

export const rightPanelConfig: Record<
  ActiveEntityType,
  GridEntityConfig<DomainObject, DomainObject>
> = {
  aid: archivalAidConfig,
  archival: archivalUnitConfig,
  ead: eadConfig,
  fund: archivalCollectionConfig,
};

export type EadTreeContextType = {
  childrenViewHeap: Record<string, EadNode[]>;
  setChildrenViewHeap: React.Dispatch<
    React.SetStateAction<Record<string, EadNode[]>>
  >;

  activeEntityType: ActiveEntityType | undefined;
  setActiveEntityType: React.Dispatch<
    React.SetStateAction<ActiveEntityType | undefined>
  >;

  currentArchivalAid: ArchivalAidMetadata | undefined;
  setCurrentArchivalAid: React.Dispatch<
    React.SetStateAction<ArchivalAidMetadata | undefined>
  >;

  currentArchivalUnit: ArchivalUnitCopy | undefined;
  setCurrentArchivalUnit: React.Dispatch<
    React.SetStateAction<ArchivalUnitCopy | undefined>
  >;

  currentArchivalCollection: ArchivalCollection | undefined;
  setCurrentArchivalCollection: React.Dispatch<
    React.SetStateAction<ArchivalCollection | undefined>
  >;

  currentEadNode: EadNode | undefined;
  setCurrentEadNode: React.Dispatch<React.SetStateAction<EadNode | undefined>>;

  crumbs: GridBreadcrumb[] | undefined;
  setCrumbs: React.Dispatch<React.SetStateAction<GridBreadcrumb[] | undefined>>;

  nextIdsToExpand: string[];
  setNextIdsToExpand: React.Dispatch<React.SetStateAction<string[]>>;

  loading: boolean;
  setLoading: React.Dispatch<React.SetStateAction<boolean>>;

  expandedNodesIdsHEAP: Record<string, boolean>;
  setExpandedNodesIdsHEAP: React.Dispatch<
    React.SetStateAction<Record<string, boolean>>
  >;

  originatorFilter?: { params: Params; originator: Originator };
  setOriginatorFilter: React.Dispatch<
    React.SetStateAction<{ params: Params; originator: Originator } | undefined>
  >;
};

const EadTreeContext = createContext<Partial<EadTreeContextType>>({});

export const EadTreeContextProvider: React.FC<{
  children?: React.ReactNode;
}> = ({ children }) => {
  const [expandedNodesIdsHEAP, setExpandedNodesIdsHEAP] = useState<
    Record<string, boolean>
  >({});

  const [activeEntityType, setActiveEntityType] = useState<
    ActiveEntityType | undefined
  >();

  const [nextIdsToExpand, setNextIdsToExpand] = useState<string[]>([]);
  const [childrenViewHeap, setChildrenViewHeap] = useState<
    Record<string, EadNode[]>
  >({});
  const [loading, setLoading] = useState<boolean>(false);
  const [currentArchivalAid, setCurrentArchivalAid] = useState<
    ArchivalAidMetadata
  >();

  const [currentArchivalCollection, setCurrentArchivalCollection] = useState<
    ArchivalCollection
  >();

  const [currentArchivalUnit, setCurrentArchivalUnit] = useState<
    ArchivalUnitCopy
  >();

  const [currentEadNode, setCurrentEadNode] = useState<EadNode>();

  const [crumbs, setCrumbs] = useState<GridBreadcrumb[]>();
  const [originatorFilter, setOriginatorFilter] = useState<{
    params: Params;
    originator: Originator;
  }>();

  const ctx: EadTreeContextType = useMemo(
    () => ({
      crumbs,
      setCrumbs,
      currentEadNode,
      setCurrentEadNode,
      currentArchivalAid,
      setCurrentArchivalAid,
      loading,
      setLoading,
      childrenViewHeap,
      setChildrenViewHeap,
      nextIdsToExpand,
      setNextIdsToExpand,
      expandedNodesIdsHEAP,
      setExpandedNodesIdsHEAP,
      currentArchivalCollection,
      setCurrentArchivalCollection,
      originatorFilter,
      setOriginatorFilter,
      currentArchivalUnit,
      setCurrentArchivalUnit,
      activeEntityType,
      setActiveEntityType,
    }),
    [
      crumbs,
      setCrumbs,
      currentEadNode,
      setCurrentEadNode,
      loading,
      childrenViewHeap,
      setChildrenViewHeap,
      currentArchivalAid,
      setCurrentArchivalAid,
      nextIdsToExpand,
      setNextIdsToExpand,
      expandedNodesIdsHEAP,
      setExpandedNodesIdsHEAP,
      currentArchivalCollection,
      setCurrentArchivalCollection,
      originatorFilter,
      setOriginatorFilter,
      currentArchivalUnit,
      setCurrentArchivalUnit,
      activeEntityType,
      setActiveEntityType,
    ]
  );

  return (
    <EadTreeContext.Provider value={ctx}> {children} </EadTreeContext.Provider>
  );
};

export const useEadTreeContext = () => useContext(EadTreeContext);
